import { useEffect, useMemo, useState } from 'react'
import type { Env, User } from '../api'
import { Captura } from './Captura'
import { Pacientes } from './Pacientes'
import { Eventos } from './Eventos'
import { Laudo } from './Laudo'
import { Laudos } from './Laudos'
import { Preview } from './Preview'
import { Relatorios } from './Relatorios'
import { Config } from './Config'
import { Backup } from './Backup'
import { Placeholder } from './Placeholder'

interface ScreenDef {
  id: string
  label: string
  icon: string
  note?: string
}

const NAV: ScreenDef[] = [
  { id: 'eventos', label: 'Eventos do Dia', icon: 'M3 4h14v13H3z M3 8h14M7 2.5v3M13 2.5v3' },
  { id: 'captura', label: 'Captura de Imagens', icon: 'M2.5 5h15v11h-15z M10 10.5a3 3 0 100-6 3 3 0 000 6z' },
  { id: 'laudo', label: 'Laudos', icon: 'M4.5 2.5h11v15h-11z M7 6.5h6M7 9.5h6M7 12.5h3.5' },
  { id: 'relatorios', label: 'Relatórios', icon: 'M3 17h14 M6 17V9M10 17V4M14 17v-5' },
  { id: 'pacientes', label: 'Pacientes', icon: 'M10 9a3 3 0 100-6 3 3 0 000 6z M4 17.5c0-3 2.7-5 6-5s6 2 6 5' },
  { id: 'config', label: 'Configurações', icon: 'M10 7.4a2.6 2.6 0 100 5.2 2.6 2.6 0 000-5.2z M10 3v2M10 15v2M3 10h2M15 10h2' },
  { id: 'backup', label: 'Backup e Sync', icon: 'M4 12.5a3.5 3.5 0 011.2-6.8A4.5 4.5 0 0114 6.6a3 3 0 01.6 5.9 M10 9v7M7.5 13.5L10 16l2.5-2.5' },
  { id: 'ajuda', label: 'Ajuda', icon: 'M10 2.5a7.5 7.5 0 100 15 7.5 7.5 0 000-15z M8 8a2 2 0 114 0c0 1.4-2 1.6-2 3M10 14.2v.3' },
]

const NOTES: Record<string, string> = {
  ajuda: 'Atalhos: ESPAÇO captura (na tela de Captura) · F2 Pacientes · F3 Configurações. O pedal de captura emula a barra de espaço.',
  fluxo: 'Mapa da jornada: agenda → captura → laudo → liberação → entrega.',
}

// "Eventos do Dia" é a tela inicial: fica sempre aberta, por isso não tem X.
const HOME = 'eventos'

export interface ActiveExam {
  id: string
  patientId: string
  patientName: string
}

export function Shell({ user, env, sessionVia, theme, onToggleTheme, onLogout }: {
  user: User
  env: Env | null
  sessionVia: 'online' | 'offline'
  theme: 'light' | 'dark'
  onToggleTheme: () => void
  onLogout: () => void
}) {
  const [screen, setScreen] = useState(HOME)
  const [openTabs, setOpenTabs] = useState<string[]>([HOME])
  const [collapsed, setCollapsed] = useState(false)
  const [exam, setExam] = useState<ActiveExam | null>(null)
  const [reportId, setReportId] = useState('')
  const online = env?.mode === 'hub'
  const nav = useMemo(() => (online ? NAV.filter((n) => n.id !== 'backup') : NAV), [online])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return
      if (e.key === 'F2') { e.preventDefault(); open('pacientes') }
      if (e.key === 'F3') { e.preventDefault(); open('config') }
      if (e.key === 'F4') { e.preventDefault(); open('eventos') }
      if (e.key === 'F9') { e.preventDefault(); setCollapsed((c) => !c) }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const open = (id: string) => {
    setScreen(id)
    setOpenTabs((tabs) => (tabs.includes(id) ? tabs : [...tabs, id]))
  }

  // Fechar a aba Laudos fecha também o laudo que estava aberto (e a
  // visualização dele): ao voltar à seção, o que aparece é a lista.
  const closeTab = (id: string) => {
    if (id === HOME) return
    const gone = id === 'laudo' ? ['laudo', 'preview'] : [id]
    const next = openTabs.filter((t) => !gone.includes(t))
    setOpenTabs(next)
    if (id === 'laudo') setReportId('')
    if (gone.includes(screen)) setScreen(next[next.length - 1] ?? HOME)
  }

  // Menu lateral: clicar em Laudos já estando no laudo aberto volta à lista.
  const go = (id: string) => {
    if (id === 'laudo' && screen === 'laudo') setReportId('')
    open(id)
  }

  const title = useMemo(() => NAV.find((n) => n.id === screen)?.label ?? screen, [screen])
  const expanded = !collapsed

  function renderScreen() {
    switch (screen) {
      case 'eventos':
        return (
          <Eventos
            user={user}
            onOpenCapture={(examId, patientId, patientName) => {
              setExam({ id: examId, patientId, patientName })
              open('captura')
            }}
            onOpenReport={(id) => { setReportId(id); open('laudo') }}
          />
        )
      case 'captura':
        return (
          <Captura
            exam={exam}
            onExamStarted={(examId, patientId, patientName) => setExam({ id: examId, patientId, patientName })}
            onOpenReport={(id) => { setReportId(id); open('laudo') }}
          />
        )
      case 'laudo':
        return reportId
          ? <Laudo reportId={reportId} user={user} onBack={() => setReportId('')} onOpenPreview={(id) => { setReportId(id); open('preview') }} />
          : <Laudos onOpenReport={setReportId} />
      case 'preview':
        return reportId
          ? <Preview reportId={reportId} onClose={() => open('laudo')} />
          : <Placeholder title="Nada para visualizar" note="Abra um laudo e toque em Visualizar." />
      case 'relatorios':
        return <Relatorios onOpenReport={(id) => { setReportId(id); open('laudo') }} />
      case 'pacientes':
        return (
          <Pacientes
            user={user}
            onStartExam={(examId, patientId, patientName) => {
              setExam({ id: examId, patientId, patientName })
              open('captura')
            }}
          />
        )
      case 'config':
        return <Config user={user} env={env} />
      case 'backup':
        return <Backup user={user} />
      default:
        return <Placeholder title={title} note={NOTES[screen] ?? 'Tela do mapa do produto — ainda não implementada.'} />
    }
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg)', color: 'var(--text)', overflow: 'hidden' }}>
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <aside className="no-print" style={{ width: expanded ? 212 : 54, flex: 'none', background: 'var(--surface)', borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', minHeight: 0, transition: 'width .12s' }}>
          <div style={{ height: 46, flex: 'none', display: 'flex', alignItems: 'center', gap: 9, padding: '0 12px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ width: 24, height: 24, flex: 'none', borderRadius: 6, background: 'var(--primary)', color: 'var(--primary-fg)', display: 'flex', alignItems: 'center', justifyContent: 'center', font: '700 12px/1 var(--font-mono)' }}>C</div>
            {expanded && (
              <div style={{ font: '600 14px/1 var(--font-sans)', letterSpacing: '-.01em', whiteSpace: 'nowrap' }}>
                CScan <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>Gastro</span>
              </div>
            )}
          </div>

          <nav style={{ flex: 1, overflowY: 'auto', padding: '8px 8px 4px', display: 'flex', flexDirection: 'column', gap: 1, minHeight: 0 }}>
            {nav.map((item) => (
              <button key={item.id} className="nav-item" data-active={screen === item.id ? '1' : '0'} onClick={() => go(item.id)} title={item.label}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" style={{ flex: 'none' }}>
                  <path d={item.icon} />
                </svg>
                {expanded && <span style={{ flex: 1, whiteSpace: 'nowrap' }}>{item.label}</span>}
              </button>
            ))}
            <button className="nav-item" onClick={onLogout} title="Sair">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" style={{ flex: 'none' }}>
                <path d="M12 3.5H5.5v13H12M10 10h7M14.5 7.5L17 10l-2.5 2.5" />
              </svg>
              {expanded && <span>Sair</span>}
            </button>
          </nav>

          <div style={{ flex: 'none', borderTop: '1px solid var(--border)', padding: 8 }}>
            <button className="nav-item" onClick={() => setCollapsed((c) => !c)}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.4" style={{ flex: 'none' }}>
                <path d="M3 4v12M7 10h9M10.5 6.5L7 10l3.5 3.5" />
              </svg>
              {expanded && <span>Recolher</span>}
            </button>
          </div>
        </aside>

        <main style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
          <div className="no-print" style={{ flex: 'none', height: 38, display: 'flex', alignItems: 'stretch', background: 'var(--surface-2)', borderBottom: '1px solid var(--border)' }}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'stretch', overflowX: 'auto', overflowY: 'hidden', minWidth: 0, height: 38 }}>
              {openTabs.map((id) => {
                const def = NAV.find((n) => n.id === id)
                return (
                  <div key={id} className="tab" data-active={screen === id ? '1' : '0'} onClick={() => setScreen(id)} style={{ height: 38 }}>
                    <span style={{ flex: 1, font: '500 12px/1 var(--font-sans)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {def?.label ?? id}
                    </span>
                    {id !== HOME && (
                      <button
                        onClick={(e) => { e.stopPropagation(); closeTab(id) }}
                        style={{ width: 17, height: 17, flex: 'none', border: 'none', borderRadius: 4, background: 'transparent', color: 'var(--text-3)', font: '400 13px/1 var(--font-sans)', cursor: 'pointer' }}
                      >
                        ×
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
            <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 6, padding: '0 10px', borderLeft: '1px solid var(--border)' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 5, font: '400 10.5px/1 var(--font-mono)', color: sessionVia === 'offline' ? 'var(--accent)' : 'var(--text-3)' }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: sessionVia === 'offline' ? 'var(--accent)' : env ? 'var(--ok)' : 'var(--danger)' }} />
                {sessionVia === 'offline' ? 'sessão offline' : !env ? 'sem conexão' : online ? 'Online' : 'Salvo local'}
              </span>
              <button className="btn" onClick={onToggleTheme} style={{ height: 24, padding: '0 9px', font: '500 10.5px/1 var(--font-mono)' }}>
                {theme === 'dark' ? 'TEMA ESCURO' : 'TEMA CLARO'}
              </button>
            </div>
          </div>

          <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
            <div key={screen + reportId + (exam?.id ?? '')} className="fade-in" style={{ position: 'absolute', inset: 0 }}>
              {renderScreen()}
            </div>
          </div>

          <footer className="no-print" style={{ flex: 'none', height: 26, display: 'flex', alignItems: 'center', gap: 14, padding: '0 12px', background: 'var(--surface)', borderTop: '1px solid var(--border)', font: '400 10.5px/1 var(--font-mono)', color: 'var(--text-3)' }}>
            <span>{user.name} · {user.role === 'admin' ? 'Administrador' : user.role}</span>
            <span style={{ width: 1, height: 11, background: 'var(--border)' }} />
            <span>CScan Gastro 8.0.0</span>
            <span style={{ flex: 1 }} />
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--ok)' }} />
              {!env ? 'healthz indisponível' : online ? 'Nuvem CScan' : `Nó ${env.node} · operação local`}
            </span>
            {exam && (
              <>
                <span style={{ width: 1, height: 11, background: 'var(--border)' }} />
                <span>Exame: {exam.patientName}</span>
              </>
            )}
          </footer>
        </main>
      </div>
    </div>
  )
}
